import { create } from 'zustand';
import { applyNativeTheme } from './native';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'heritage.theme';

function isTheme(value: string | null): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

function storedTheme(): Theme {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (isTheme(value)) return value;
  } catch {
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    Boolean(window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  );
}

function resolveDark(theme: Theme): boolean {
  return theme === 'dark' || (theme === 'system' && systemPrefersDark());
}

function applyTheme(theme: Theme): boolean {
  const dark = resolveDark(theme);
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', dark);
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', dark ? '#171310' : '#755c42');
  }
  void applyNativeTheme(dark);
  return dark;
}

interface ThemeState {
  theme: Theme;
  dark: boolean;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const initialTheme = storedTheme();

export const useTheme = create<ThemeState>((set, get) => ({
  theme: initialTheme,
  dark: resolveDark(initialTheme),
  setTheme: (theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
    }
    const dark = applyTheme(theme);
    set({ theme, dark });
  },
  toggle: () => {
    const next: Theme = get().dark ? 'light' : 'dark';
    get().setTheme(next);
  },
}));

let initialized = false;

export function initTheme(): void {
  const { theme } = useTheme.getState();
  const dark = applyTheme(theme);
  useTheme.setState({ dark });
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  const media = window.matchMedia?.('(prefers-color-scheme: dark)');
  if (!media) return;
  const handleChange = () => {
    if (useTheme.getState().theme !== 'system') return;
    const nextDark = applyTheme('system');
    useTheme.setState({ dark: nextDark });
  };
  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', handleChange);
  } else {
    media.addListener?.(handleChange);
  }
}
